/**
 * Operational dashboard broker — GET /dashboard aggregate + action helpers.
 */
import AxiosManager from '@/utils/AxiosManager'
import Constants from '@/helpers/Constants'

export interface DashboardPeriod {
  id: number
  name: string
  status: string
  is_trial: boolean
  period_start: string | null
  period_end: string | null
}

export interface DashboardVariable {
  name: string
  value: number | null
  unit: string
  var_class: string
  description: string
}

export interface DashboardTcm {
  available: boolean
  nee_n_act: number | null
  nee_n_deemed: number | null
  tpn: number | null
  vat: number | null
  invoice_total: number | null
  valid_hour_count: number
  variables: DashboardVariable[]
}

export interface DashboardPpmSummary {
  sample_count: number
  valid_hours: number
  invalid_hours: number
  hour_count: number
  avg_pr: number | null
  avg_apr?: number | null
  avg_ppr?: number | null
  avg_prd?: number | null
  tc_metrics_available?: boolean
}

export interface DashboardPpmHourly {
  hour_start: string
  pr: number | null
  apr: number | null
  ppr: number | null
  is_valid: boolean
}

export interface DashboardPpm {
  available: boolean
  summary: DashboardPpmSummary
  hourly?: DashboardPpmHourly[]
}

export interface DashboardEnergyPoint {
  hour_start: string
  nee_act: number | null
  nee_deemed: number | null
  gpoa: number | null
  is_valid: boolean
}

export interface DashboardPaymentPoint {
  period_id: number
  period_name: string
  period_start: string | null
  tpn: number | null
  vat: number | null
  invoice_total: number | null
  nee_n_act: number | null
  nee_n_deemed: number | null
}

export interface DashboardInvoice {
  id: number
  billing_period_id: number
  invoice_number: string | null
  invoice_date: string | null
  mode: string | null
  status: string | null
  pdf_filename: string | null
  generated_by: string | null
  approved_by: string | null
  approved_at: string | null
  created_at: string | null
}

export interface DashboardKpis {
  period_name: string | null
  period_status: string | null
  is_trial: boolean
  nee_n_act: number | null
  nee_n_deemed: number | null
  nee_gap_kwh?: number | null
  nee_gap_pct?: number | null
  tpn: number | null
  vat: number | null
  invoice_total: number | null
  valid_hour_count: number | null
  avg_pr: number | null
  avg_apr: number | null
  avg_ppr: number | null
  valid_hours: number | null
  invalid_hours: number | null
  tcm_available: boolean
  ppm_available: boolean
  prev_period_name?: string | null
  prev_tpn?: number | null
  prev_nee_n_act?: number | null
  prev_invoice_total?: number | null
}

export interface DashboardActions {
  pending_manual_entries: number
  draft_invoices: number
  invalid_hours: number
  telemetry_pending: number
  telemetry_failed: number
  manual_entries_recent: Array<{
    id: number
    variable_name: string
    period_name: string
    value: number | null
    status: string
    submitted_by_username: string | null
    submitted_at: string | null
  }>
  invalid_hours_recent: Array<{
    hour_start: string | null
    invalid_reason: string | null
    sample_count: number | null
    valid_sample_count: number | null
  }>
  draft_invoices_list: DashboardInvoice[]
}

export interface DashboardHealthComponent {
  key: string
  label: string
  live: number
  simulated_offline: boolean
  effective: number
}

export interface DashboardValidityCell {
  day: string
  hour: number
  is_valid: boolean
}

export interface DashboardCoefficients {
  Ai: number | null
  Bi: number | null
  Ci: number | null
  pending_count: number
  pending_proposals: Array<{
    id: number
    target_period: string | null
    status: string
    ai: number | null
    bi: number | null
    ci: number | null
    data_points: number | null
    generated_at: string | null
    generated_by: string | null
  }>
}

export interface DashboardPayload {
  periods: DashboardPeriod[]
  selected_period: DashboardPeriod | null
  kpis: DashboardKpis | null
  tcm: DashboardTcm | null
  ppm: DashboardPpm | null
  energy_series: DashboardEnergyPoint[] | null
  payments_series: DashboardPaymentPoint[] | null
  invoices: DashboardInvoice[] | null
  actions: DashboardActions | null
  health: {
    ts: string
    components: DashboardHealthComponent[]
    any_offline: boolean
    alert_simulation?: boolean
  } | null
  telemetry: {
    pending: number
    failed: number
    sent: number
    dispatcher_running: boolean
    dispatcher?: Record<string, unknown>
  } | null
  scheduler: {
    running: boolean
    paused: boolean
    reason?: string
    last_processed_hour?: string | null
    auto_ppm?: boolean
    auto_tcm?: boolean
    auto_invoice?: boolean
  } | null
  mda: {
    last_ingest_at: string | null
    age_seconds: number | null
    samples_last_24h: number
  } | null
  dcs: {
    ts?: string
    ambient_temp?: number | null
    humidity?: number | null
    pressure?: number | null
    wind_speed?: number | null
    irradiance?: number | null
  } | null
  validity_heatmap: DashboardValidityCell[] | null
  coefficients: DashboardCoefficients | null
  formula_set: {
    read_only: boolean
    version: string
    checksum: string
    count: number
  } | null
  charge_breakdown: Array<{ name: string; value: number | null }> | null
  timeline: Array<{ at: string | null; kind: string; label: string }> | null
  audit_recent: string[] | null
}

export default class DashboardBroker {
  private static get ax(): AxiosManager {
    return new AxiosManager(Constants.SERVER_URL)
  }

  public static async get(billing_period_id?: number | null): Promise<DashboardPayload> {
    let url = '/dashboard'
    if (billing_period_id != null) {
      url += `?billing_period_id=${billing_period_id}`
    }
    return await DashboardBroker.ax.get(url)
  }

  public static async runTcm(billing_period_id: number): Promise<{
    nee_n_act: number
    nee_n_deemed: number
    tpn: number
    vat: number
    invoice_total: number
    valid_hour_count: number
  }> {
    return await DashboardBroker.ax.post('/run_tcm', { billing_period_id })
  }

  public static async runPpm(billing_period_id: number): Promise<{
    sample_count: number
    valid_hours: number
    invalid_hours: number
  }> {
    return await DashboardBroker.ax.post('/run_ppm', { billing_period_id })
  }

  public static async generateInvoice(billing_period_id: number, mode = 'final'): Promise<any> {
    return await DashboardBroker.ax.post('/generate_invoice', {
      billing_period_id,
      mode,
      comp: 'invoice',
    })
  }

  public static async approveInvoice(invoice_id: number): Promise<any> {
    return await DashboardBroker.ax.post('/approve_invoice', { invoice_id })
  }

  public static async approveManualEntry(id: number): Promise<any> {
    return await DashboardBroker.ax.post('/approve_manual_entry', { id })
  }

  public static async rejectManualEntry(id: number): Promise<any> {
    return await DashboardBroker.ax.post('/reject_manual_entry', { id })
  }

  public static async generateReport(
    report_type: string,
    billing_period_id: number,
  ): Promise<any> {
    return await DashboardBroker.ax.post('/generate_report', {
      report_type,
      billing_period_id,
      format: 'both',
    })
  }

  public static async flushTelemetry(): Promise<any> {
    return await DashboardBroker.ax.post('/flush_telemetry', {})
  }

  public static async schedulerControl(action: string): Promise<any> {
    return await DashboardBroker.ax.post('/scheduler_control', { action })
  }
}
